import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { alpha } from '@mui/material/styles';

import SearchRounded from '@mui/icons-material/SearchRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import PaymentOutlined from '@mui/icons-material/PaymentOutlined';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import PendingOutlined from '@mui/icons-material/PendingOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';

import PageHeader from '../components/common/PageHeader';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  getConsultancyStudents,
  updateConsultancy,
  updateStudentPayout,
} from '../services/consultancyService';

const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null;
  return <Box sx={{ pt: 2.5 }}>{children}</Box>;
};

const InfoItem = ({ icon, label, value, copyable }) => {
  if (!value) return null;
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      <Box sx={{ color: '#9CA3AF', mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.9rem', color: '#1F2937', fontWeight: 500 }}>
            {value}
          </Typography>
          {copyable && (
            <Tooltip title="Copy">
              <IconButton size="small" onClick={handleCopy} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                <ContentCopyOutlined sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const StatCard = ({ label, value, color, icon, subtitle }) => (
  <Paper sx={{ px: 3, py: 2.5, flex: 1, minWidth: 160, position: 'relative', overflow: 'hidden' }}>
    <Box sx={{ position: 'absolute', top: 12, right: 16, color: alpha(color, 0.15) }}>
      {icon}
    </Box>
    <Typography sx={{ fontSize: '0.7rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', mt: 0.25 }}>
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const ConsultancyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [consultancy, setConsultancy] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, paid
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Payout dialog
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutStudent, setPayoutStudent] = useState(null);
  const [payoutData, setPayoutData] = useState({
    consultancyPayout: '',
    consultancyPayoutStatus: 'pending',
    consultancyPayoutDate: '',
    receipt: null,
  });
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);

  // Receipt viewer
  const [receiptViewOpen, setReceiptViewOpen] = useState(false);
  const [receiptViewUrl, setReceiptViewUrl] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getConsultancyStudents(id);
      setConsultancy(res.data?.data?.consultancy || null);
      setStudents(res.data?.data?.students || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load consultancy details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // --- Computed Stats ---
  const stats = useMemo(() => {
    const total = students.length;
    const totalPayout = students.reduce((sum, s) => sum + (s.consultancyPayout || 0), 0);
    const paidStudents = students.filter((s) => s.consultancyPayoutStatus === 'paid');
    const paidPayout = paidStudents.reduce((sum, s) => sum + (s.consultancyPayout || 0), 0);
    const pendingPayout = totalPayout - paidPayout;
    const totalLoan = students.reduce((sum, s) => sum + (s.loanAmount || 0), 0);
    return { total, totalPayout, paidPayout, pendingPayout, paidCount: paidStudents.length, pendingCount: total - paidStudents.length, totalLoan };
  }, [students]);

  // --- Filtered & Sorted Students ---
  const filteredStudents = useMemo(() => {
    let result = [...students];

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) ||
          s.fileNumber?.toLowerCase().includes(q) ||
          s.phone?.includes(q) ||
          s.email?.toLowerCase().includes(q) ||
          s.course?.toLowerCase().includes(q) ||
          s.country?.toLowerCase().includes(q)
      );
    }

    // Filter by payout status
    if (filterStatus === 'pending') {
      result = result.filter((s) => s.consultancyPayoutStatus !== 'paid');
    } else if (filterStatus === 'paid') {
      result = result.filter((s) => s.consultancyPayoutStatus === 'paid');
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'consultancyPayout' || sortField === 'loanAmount') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [students, search, filterStatus, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // --- Edit Consultancy ---
  const handleOpenEdit = () => {
    setEditForm({
      name: consultancy.name || '',
      contactPerson: consultancy.contactPerson || '',
      phone: consultancy.phone || '',
      email: consultancy.email || '',
      address: consultancy.address || '',
      accountNumber: consultancy.accountNumber || '',
      ifscCode: consultancy.ifscCode || '',
      bankName: consultancy.bankName || '',
      accountHolderName: consultancy.accountHolderName || '',
      upiVpaId: consultancy.upiVpaId || '',
      isActive: consultancy.isActive !== false,
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editForm.name.trim()) return;
    try {
      setEditSaving(true);
      await updateConsultancy(id, editForm);
      setEditDialogOpen(false);
      setSnackbar({ open: true, message: 'Consultancy updated successfully', severity: 'success' });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update', severity: 'error' });
    } finally {
      setEditSaving(false);
    }
  };

  // --- Payout Management ---
  const handleOpenPayout = (student) => {
    setPayoutStudent(student);
    setPayoutData({
      consultancyPayout: student.consultancyPayout || '',
      consultancyPayoutStatus: student.consultancyPayoutStatus || 'pending',
      consultancyPayoutDate: student.consultancyPayoutDate
        ? new Date(student.consultancyPayoutDate).toISOString().split('T')[0]
        : '',
      receipt: null,
    });
    setReceiptPreview(student.consultancyPayoutReceipt || null);
    setPayoutDialogOpen(true);
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPayoutData({ ...payoutData, receipt: file });
      setReceiptPreview(URL.createObjectURL(file));
    }
  };

  const handlePayoutSave = async () => {
    if (!payoutStudent) return;
    try {
      setPayoutSaving(true);
      await updateStudentPayout(payoutStudent._id, payoutData);
      setPayoutDialogOpen(false);
      setPayoutStudent(null);
      setReceiptPreview(null);
      setSnackbar({ open: true, message: 'Payout updated successfully', severity: 'success' });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update payout', severity: 'error' });
    } finally {
      setPayoutSaving(false);
    }
  };

  // --- Receipt Viewer ---
  const handleViewReceipt = (url) => {
    setReceiptViewUrl(url);
    setReceiptViewOpen(true);
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!consultancy) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Consultancy not found</Alert>
      </Box>
    );
  }

  const hasBankingDetails = consultancy.accountNumber || consultancy.ifscCode || consultancy.bankName || consultancy.upiVpaId;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <PageHeader
        title={consultancy.name}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Consultancy', link: '/consultancies' },
          { label: consultancy.name },
        ]}
      >
        {!consultancy.isActive && (
          <Chip label="Inactive" size="small" sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 600 }} />
        )}
        <Button variant="outlined" startIcon={<EditOutlined />} onClick={handleOpenEdit}>
          Edit Details
        </Button>
      </PageHeader>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard
          label="Total Students"
          value={stats.total}
          color="#2C7BE5"
          icon={<SchoolOutlined sx={{ fontSize: 40 }} />}
          subtitle={`Total Loan: ${formatAmount(stats.totalLoan)}`}
        />
        <StatCard
          label="Total Payout"
          value={formatAmount(stats.totalPayout)}
          color="#F59E0B"
          icon={<TrendingUpOutlined sx={{ fontSize: 40 }} />}
          subtitle={`${stats.total} students`}
        />
        <StatCard
          label="Paid"
          value={formatAmount(stats.paidPayout)}
          color="#10B981"
          icon={<CheckCircleOutline sx={{ fontSize: 40 }} />}
          subtitle={`${stats.paidCount} students`}
        />
        <StatCard
          label="Pending"
          value={formatAmount(stats.pendingPayout)}
          color="#EF4444"
          icon={<PendingOutlined sx={{ fontSize: 40 }} />}
          subtitle={`${stats.pendingCount} students`}
        />
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ px: 2, borderBottom: '1px solid #E5E7EB' }}
        >
          <Tab label={`Students (${stats.total})`} />
          <Tab label="Consultancy Details" />
          {hasBankingDetails && <Tab label="Banking Details" />}
        </Tabs>

        {/* Tab 0: Students */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 2, pb: 2 }}>
            {/* Search & Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded sx={{ color: '#9CA3AF', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { backgroundColor: '#F9FAFB' } }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                {['all', 'pending', 'paid'].map((f) => (
                  <Chip
                    key={f}
                    label={f === 'all' ? `All (${stats.total})` : f === 'paid' ? `Paid (${stats.paidCount})` : `Pending (${stats.pendingCount})`}
                    onClick={() => setFilterStatus(f)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      bgcolor: filterStatus === f
                        ? (f === 'paid' ? alpha('#10B981', 0.15) : f === 'pending' ? alpha('#F59E0B', 0.15) : alpha('#2C7BE5', 0.15))
                        : '#F3F4F6',
                      color: filterStatus === f
                        ? (f === 'paid' ? '#10B981' : f === 'pending' ? '#F59E0B' : '#2C7BE5')
                        : '#6B7280',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.85 },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Students Table */}
            {filteredStudents.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography sx={{ color: '#9CA3AF' }}>
                  {search || filterStatus !== 'all' ? 'No students match your filters' : 'No students linked to this consultancy yet'}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>
                        <TableSortLabel active={sortField === 'fileNumber'} direction={sortField === 'fileNumber' ? sortDir : 'asc'} onClick={() => handleSort('fileNumber')}>
                          File No.
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>
                        <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>
                          Student
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>Course / Country</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>
                        <TableSortLabel active={sortField === 'loanAmount'} direction={sortField === 'loanAmount' ? sortDir : 'asc'} onClick={() => handleSort('loanAmount')}>
                          Loan Amount
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>
                        <TableSortLabel active={sortField === 'consultancyPayout'} direction={sortField === 'consultancyPayout' ? sortDir : 'asc'} onClick={() => handleSort('consultancyPayout')}>
                          Payout
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>Payout Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>Payout Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>Receipt</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', textTransform: 'uppercase' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStudents.map((s) => (
                      <TableRow key={s._id} hover>
                        <TableCell
                          sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#2C7BE5', cursor: 'pointer' }}
                          onClick={() => navigate(`/students/${s._id}`)}
                        >
                          {s.fileNumber || '-'}
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1F2937', cursor: 'pointer', '&:hover': { color: '#2C7BE5' } }}
                            onClick={() => navigate(`/students/${s._id}`)}
                          >
                            {s.name}
                          </Typography>
                          {s.phone && (
                            <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>{s.phone}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: '#1F2937' }}>{s.course || '-'}</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>{s.country || '-'}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1F2937' }}>
                          {formatAmount(s.loanAmount)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={s.status} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#F59E0B' }}>
                          {formatAmount(s.consultancyPayout)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={s.consultancyPayoutStatus === 'paid' ? 'Paid' : 'Pending'}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              bgcolor: s.consultancyPayoutStatus === 'paid' ? alpha('#10B981', 0.1) : alpha('#F59E0B', 0.1),
                              color: s.consultancyPayoutStatus === 'paid' ? '#10B981' : '#F59E0B',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                          {formatDate(s.consultancyPayoutDate)}
                        </TableCell>
                        <TableCell>
                          {s.consultancyPayoutReceipt ? (
                            <Tooltip title="View Receipt">
                              <IconButton size="small" onClick={() => handleViewReceipt(s.consultancyPayoutReceipt)}>
                                <VisibilityOutlined sx={{ fontSize: 18, color: '#2C7BE5' }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: '#D1D5DB' }}>-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Manage Payout">
                            <IconButton size="small" color="primary" onClick={() => handleOpenPayout(s)}>
                              <PaymentOutlined sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Student">
                            <IconButton size="small" onClick={() => navigate(`/students/${s._id}`)}>
                              <OpenInNewOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Summary row */}
            {filteredStudents.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 2, pt: 2, borderTop: '1px solid #E5E7EB' }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                  Showing {filteredStudents.length} of {students.length} students
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1F2937' }}>
                  Total Payout: <span style={{ color: '#F59E0B' }}>{formatAmount(filteredStudents.reduce((s, st) => s + (st.consultancyPayout || 0), 0))}</span>
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Tab 1: Consultancy Details */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 3, pb: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 3, border: '1px solid #E5E7EB' }} elevation={0}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
                    Contact Information
                  </Typography>
                  <InfoItem icon={<PersonOutlined sx={{ fontSize: 20 }} />} label="Contact Person" value={consultancy.contactPerson} />
                  <InfoItem icon={<PhoneOutlined sx={{ fontSize: 20 }} />} label="Phone" value={consultancy.phone} copyable />
                  <InfoItem icon={<EmailOutlined sx={{ fontSize: 20 }} />} label="Email" value={consultancy.email} copyable />
                  <InfoItem icon={<LocationOnOutlined sx={{ fontSize: 20 }} />} label="Address" value={consultancy.address} />
                  {!consultancy.contactPerson && !consultancy.phone && !consultancy.email && !consultancy.address && (
                    <Typography sx={{ fontSize: '0.85rem', color: '#9CA3AF', py: 2 }}>No contact details added</Typography>
                  )}
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 3, border: '1px solid #E5E7EB' }} elevation={0}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
                    Other Details
                  </Typography>
                  <InfoItem icon={<SchoolOutlined sx={{ fontSize: 20 }} />} label="Total Students Referred" value={String(stats.total)} />
                  <InfoItem icon={<TrendingUpOutlined sx={{ fontSize: 20 }} />} label="Total Payout Amount" value={formatAmount(stats.totalPayout)} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Box sx={{ color: '#9CA3AF', mt: 0.25 }}><CheckCircleOutline sx={{ fontSize: 20 }} /></Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                        Status
                      </Typography>
                      <Chip
                        label={consultancy.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          mt: 0.5,
                          fontWeight: 600,
                          bgcolor: consultancy.isActive ? alpha('#10B981', 0.1) : alpha('#EF4444', 0.1),
                          color: consultancy.isActive ? '#10B981' : '#EF4444',
                        }}
                      />
                    </Box>
                  </Box>
                  <InfoItem
                    icon={<PersonOutlined sx={{ fontSize: 20 }} />}
                    label="Created"
                    value={`${formatDate(consultancy.createdAt)}`}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 2: Banking Details */}
        {hasBankingDetails && (
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 3, border: '1px solid #E5E7EB' }} elevation={0}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceOutlined sx={{ fontSize: 18 }} /> Bank Account Details
                    </Typography>
                    <InfoItem icon={<PersonOutlined sx={{ fontSize: 20 }} />} label="Account Holder Name" value={consultancy.accountHolderName} copyable />
                    <InfoItem icon={<AccountBalanceOutlined sx={{ fontSize: 20 }} />} label="Account Number" value={consultancy.accountNumber} copyable />
                    <InfoItem icon={<AccountBalanceOutlined sx={{ fontSize: 20 }} />} label="IFSC Code" value={consultancy.ifscCode} copyable />
                    <InfoItem icon={<AccountBalanceOutlined sx={{ fontSize: 20 }} />} label="Bank Name" value={consultancy.bankName} />
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 3, border: '1px solid #E5E7EB' }} elevation={0}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
                      UPI Details
                    </Typography>
                    <InfoItem icon={<PaymentOutlined sx={{ fontSize: 20 }} />} label="UPI VPA ID" value={consultancy.upiVpaId} copyable />
                    {!consultancy.upiVpaId && (
                      <Typography sx={{ fontSize: '0.85rem', color: '#9CA3AF', py: 2 }}>No UPI details added</Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
        )}
      </Paper>

      {/* Edit Consultancy Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Consultancy</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Consultancy Name"
                required
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Contact Person" value={editForm.contactPerson || ''} onChange={(e) => setEditForm({ ...editForm, contactPerson: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Phone" value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Email" type="email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Address" value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceOutlined sx={{ fontSize: 20 }} /> Banking Details
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Account Holder Name" value={editForm.accountHolderName || ''} onChange={(e) => setEditForm({ ...editForm, accountHolderName: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Account Number" value={editForm.accountNumber || ''} onChange={(e) => setEditForm({ ...editForm, accountNumber: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="IFSC Code" value={editForm.ifscCode || ''} onChange={(e) => setEditForm({ ...editForm, ifscCode: e.target.value.toUpperCase() })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Bank Name" value={editForm.bankName || ''} onChange={(e) => setEditForm({ ...editForm, bankName: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="UPI VPA ID" placeholder="e.g. name@upi" value={editForm.upiVpaId || ''} onChange={(e) => setEditForm({ ...editForm, upiVpaId: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch checked={editForm.isActive !== false} onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })} />
                }
                label="Active"
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={editSaving || !(editForm.name || '').trim()}>
            {editSaving ? 'Saving...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payout Management Dialog */}
      <Dialog open={payoutDialogOpen} onClose={() => setPayoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentOutlined /> Manage Payout
        </DialogTitle>
        <DialogContent>
          {payoutStudent && (
            <Box>
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1F2937' }}>
                  {payoutStudent.name}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                  {payoutStudent.fileNumber} &bull; Loan: {formatAmount(payoutStudent.loanAmount)}
                </Typography>
              </Paper>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Payout Amount"
                    type="number"
                    value={payoutData.consultancyPayout}
                    onChange={(e) => setPayoutData({ ...payoutData, consultancyPayout: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">INR</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Payout Status"
                    value={payoutData.consultancyPayoutStatus}
                    onChange={(e) => setPayoutData({ ...payoutData, consultancyPayoutStatus: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Payout Date"
                    type="date"
                    value={payoutData.consultancyPayoutDate}
                    onChange={(e) => setPayoutData({ ...payoutData, consultancyPayoutDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<UploadFileOutlined />}
                    sx={{ height: '56px', textTransform: 'none', justifyContent: 'flex-start' }}
                  >
                    {payoutData.receipt ? payoutData.receipt.name : 'Upload Receipt'}
                    <input type="file" hidden accept="image/*,.pdf" onChange={handleReceiptChange} />
                  </Button>
                </Grid>
                {receiptPreview && (
                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 1.5, border: '1px solid #E5E7EB', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <ReceiptLongOutlined sx={{ fontSize: 16 }} /> Receipt Preview
                      </Typography>
                      <Box
                        component="img"
                        src={receiptPreview}
                        alt="Receipt"
                        sx={{ maxWidth: '100%', maxHeight: 250, objectFit: 'contain', borderRadius: 1 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPayoutDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handlePayoutSave} variant="contained" disabled={payoutSaving}>
            {payoutSaving ? 'Saving...' : 'Save Payout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Viewer Dialog */}
      <Dialog open={receiptViewOpen} onClose={() => setReceiptViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongOutlined /> Payment Receipt
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          {receiptViewUrl && (
            <Box
              component="img"
              src={receiptViewUrl}
              alt="Payment Receipt"
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1 }}
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = document.createElement('p');
                fallback.style.color = '#6B7280';
                fallback.textContent = 'Unable to preview receipt. ';
                const link = document.createElement('a');
                link.href = receiptViewUrl;
                link.target = '_blank';
                link.rel = 'noopener';
                link.textContent = 'Open in new tab';
                fallback.appendChild(link);
                e.target.parentElement.appendChild(fallback);
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReceiptViewOpen(false)} color="inherit">Close</Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewOutlined />}
            onClick={() => window.open(receiptViewUrl, '_blank')}
          >
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConsultancyDetailPage;
